import {
    useImperativeHandle,
    useLayoutEffect,
    forwardRef,
    useEffect,
    useState,
    useMemo,
    useRef
} from "react";
import {
    AccessibilityInfo,
    Animated,
    Platform,
    View
} from "react-native";
import type {
    IMonthSelectorRef,
    CalendarMonth
} from "./type";
import type IMonthSelectorProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    MONTH_SELECTOR_NAVIGATION_KEYS,
    getMonthSelectorNavigationDate,
    getMonthSelectorSelectedDate,
    getMonthSelectorTabStopIndex,
    getMonthSelectorRangeResult,
    MONTH_SELECTOR_COLUMN_COUNT,
    getMonthSelectorMonthTitle,
    getMonthSelectorNavigation,
    getMonthSelectorPageIndex,
    getMonthSelectorMonths,
    getMonthSelectorTitle,
    getMonthSelectorRows,
    isValidDate,
    dateSelect
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitToast
} from "../../core/hooks";
import {
    type RefForwardingComponent,
    type NCoreUIKitIcon
} from "../../types";
import {
    useBreakpoint
} from "../../helpers/pointer";
import {
    useDateSelectorSwap
} from "../dateSelector/hook";
import type {
    CalendarCellKeyboardEventType
} from "../calendarCell/type";
import {
    ChevronRight as ChevronRightIcon,
    ChevronLeft as ChevronLeftIcon
} from "lucide-react-native";
import moment from "moment";
import useCalendarCellHandlers from "../calendarCell/hook";
import AnimatedIcon from "../animatedIcon";
import CalendarCell from "../calendarCell";
import Button from "../button";
import Text from "../text";

const PreviousYearIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    icon={ChevronLeftIcon}
    animation="signature"
    color={color}
    size={size}
/>;

const NextYearIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    icon={ChevronRightIcon}
    animation="signature"
    color={color}
    size={size}
/>;

const MonthSelector: RefForwardingComponent<IMonthSelectorRef, IMonthSelectorProps> = ({
    multipleSelectMinimumRequiredDayCount,
    monthOfYearLengthType = "short",
    isShowTodayIndicator = true,
    multipleSelectDayLimit,
    setIsSheetContentReady,
    viewDate: viewDateProp,
    selectMultipleObject,
    isShowCard = true,
    customLocalize,
    selectObject,
    customTheme,
    dateRange,
    maxDate,
    minDate,
    variant,
    date
}, ref) => {
    const {
        controlHeights,
        shapeConfigs,
        radiuses,
        borders,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const cellRefs = useRef<Array<View | null>>([]);

    const [
        viewDate,
        setViewDate
    ] = useState(() => isValidDate(viewDateProp) ? viewDateProp : dateSelect({
        dateRange,
        variant,
        date
    }));

    const [
        activeDate,
        setActiveDate
    ] = useState<Date | undefined>(undefined);

    const [
        focusRequest,
        setFocusRequest
    ] = useState(0);

    const months = useMemo(() => getMonthSelectorMonths({
        dateRange,
        viewDate,
        maxDate,
        minDate,
        variant,
        date
    }), [
        dateRange,
        viewDate,
        maxDate,
        minDate,
        variant,
        date
    ]);

    const isWeb = Platform.OS === "web";

    const tabStopIndex = getMonthSelectorTabStopIndex({
        activeDate,
        months
    });

    const title = getMonthSelectorTitle({
        activeLocale,
        viewDate
    });

    const announcedTitleRef = useRef(title);

    const currentNavigation = getMonthSelectorNavigation({
        viewDate,
        maxDate,
        minDate
    });

    const monthsLabel = localize("month-selector-group-label", [
        title
    ]);

    const groupLabel = variant === "range" ? localize("calendar-range-group-label", [
        monthsLabel
    ]) : monthsLabel;

    const breakpoint = useBreakpoint();

    const {
        nextPrevToolContainer: nextPrevToolContainerDynamicStyle,
        contentContainer: contentContainerDynamicStyle,
        container: containerDynamicStyle
    } = useStyles({
        isCardShown: isShowCard && breakpoint !== "compact",
        isStretched: !isShowCard,
        controlHeights,
        shapeConfigs,
        radiuses,
        borders,
        spaces,
        colors
    });

    const swapStyle = useDateSelectorSwap({
        pageIndex: getMonthSelectorPageIndex({
            viewDate
        }),
        customTheme
    });

    useLayoutEffect(() => {
        setIsSheetContentReady?.(true);
    }, [
        months
    ]);

    useLayoutEffect(() => {
        if(!focusRequest || !activeDate) {
            return;
        }

        const activeIndex = months.findIndex((monthItem) => monthItem.date.isSame(activeDate, "month"));

        cellRefs.current[activeIndex]?.focus();
    }, [
        focusRequest
    ]);

    useEffect(() => {
        if(isValidDate(viewDateProp)) {
            setViewDate(viewDateProp);
        }
    }, [
        viewDateProp
    ]);

    useEffect(() => {
        if(announcedTitleRef.current === title) {
            return;
        }

        announcedTitleRef.current = title;

        if(Platform.OS !== "ios") {
            return;
        }

        AccessibilityInfo.announceForAccessibility(title);
    }, [
        title
    ]);

    useImperativeHandle(
        ref,
        () => ({
            changeCurrentYear: (date: Date) => {
                if(!isValidDate(date)) {
                    return;
                }

                setViewDate(date);
            }
        }),
        []
    );

    const onPreviousYearPress = () => {
        setViewDate((currentViewDate) => moment(currentViewDate).subtract(1, "years").toDate());
    };

    const onNextYearPress = () => {
        setViewDate((currentViewDate) => moment(currentViewDate).add(1, "years").toDate());
    };

    const onMonthPress = (monthItem: CalendarMonth) => {
        if(variant === "single") {
            selectObject(getMonthSelectorSelectedDate({
                month: monthItem.date,
                date
            }));
            return;
        }

        if(variant !== "range") {
            return;
        }

        const rangeResult = getMonthSelectorRangeResult({
            multipleSelectMinimumRequiredDayCount,
            multipleSelectDayLimit,
            month: monthItem.date,
            dateRange
        });

        if(rangeResult.errorKey) {
            NCoreUIKitToast.open({
                title: localize(rangeResult.errorKey)
            });
            return;
        }

        selectMultipleObject(rangeResult.dateRange);
    };

    const handleActiveDateChange = (nextDate: Date) => {
        setActiveDate((currentDate) => currentDate && moment(currentDate).isSame(nextDate, "month") ? currentDate : nextDate);
    };

    const onMonthKeyDown = (event: CalendarCellKeyboardEventType, monthItem: CalendarMonth) => {
        if(event.altKey || event.ctrlKey || event.metaKey || !MONTH_SELECTOR_NAVIGATION_KEYS.includes(event.key)) {
            return;
        }

        event.preventDefault();

        const targetMonth = getMonthSelectorNavigationDate({
            month: monthItem.date,
            key: event.key,
            direction,
            maxDate,
            minDate
        });

        if(!targetMonth) {
            return;
        }

        setFocusRequest((currentRequest) => currentRequest + 1);

        handleActiveDateChange(targetMonth.toDate());

        if(!targetMonth.isSame(viewDate, "year")) {
            setViewDate(targetMonth.toDate());
        }
    };

    const getMonthTabIndex = (monthIndex: number): 0 | -1 => {
        return monthIndex === tabStopIndex ? 0 : -1;
    };

    const getCellHandlers = useCalendarCellHandlers({
        onFocus: (monthIndex) => {
            const monthItem = months[monthIndex];

            if(monthItem) {
                handleActiveDateChange(monthItem.date.toDate());
            }
        },
        onKeyDown: (event, monthIndex) => {
            const monthItem = months[monthIndex];

            if(monthItem) {
                onMonthKeyDown(event, monthItem);
            }
        },
        onPress: (monthIndex) => {
            const monthItem = months[monthIndex];

            if(monthItem) {
                onMonthPress(monthItem);
            }
        },
        cellRefs
    });

    const renderMonth = (monthItem: CalendarMonth, monthIndex: number) => {
        const cellHandlers = getCellHandlers(monthIndex);

        return <CalendarCell
            tabIndex={isWeb ? getMonthTabIndex(monthIndex) : undefined}
            onKeyDown={isWeb ? cellHandlers.onKeyDown : undefined}
            isToday={isShowTodayIndicator && monthItem.isToday}
            onFocus={isWeb ? cellHandlers.onFocus : undefined}
            rangePosition={monthItem.rangePosition}
            title={getMonthSelectorMonthTitle({
                date: monthItem.date.toDate(),
                monthOfYearLengthType,
                activeLocale
            })}
            isDisabled={monthItem.isDisabled}
            isSelected={monthItem.isSelected}
            customLocalize={customLocalize}
            date={monthItem.date.toDate()}
            onPress={cellHandlers.onPress}
            key={`month-${monthIndex}`}
            customTheme={customTheme}
            spreadBehaviour="stretch"
            ref={cellHandlers.ref}
            calendarType="month"
        />;
    };

    const renderNextPrevTool = () => {
        return <View
            style={[
                stylesheet.nextPrevToolContainer,
                nextPrevToolContainerDynamicStyle
            ]}
        >
            <Button
                isDisabled={currentNavigation.isPreviousDisabled}
                accessibilityLabel={localize("previous-year")}
                onPress={onPreviousYearPress}
                customTheme={customTheme}
                icon={PreviousYearIcon}
                spreadBehaviour="free"
                variant="ghost"
                type="neutral"
                size="medium"
            />
            <Text
                customTheme={customTheme}
                variant="titleMediumSize"
                style={stylesheet.title}
                aria-live="polite"
                numberOfLines={1}
                color="high"
            >
                {title}
            </Text>
            <Button
                isDisabled={currentNavigation.isNextDisabled}
                accessibilityLabel={localize("next-year")}
                onPress={onNextYearPress}
                customTheme={customTheme}
                spreadBehaviour="free"
                icon={NextYearIcon}
                variant="ghost"
                type="neutral"
                size="medium"
            />
        </View>;
    };

    const renderMonths = () => {
        return getMonthSelectorRows(months).map((rowMonths, rowIndex) => {
            return <View
                key={`month-row-${rowIndex}`}
                style={[
                    stylesheet.rowContainer
                ]}
            >
                {rowMonths.map((monthItem, monthIndex) => renderMonth(monthItem, rowIndex * MONTH_SELECTOR_COLUMN_COUNT + monthIndex))}
            </View>;
        });
    };

    return <View
        style={[
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        {renderNextPrevTool()}
        <Animated.View
            style={[
                stylesheet.contentContainer,
                contentContainerDynamicStyle,
                swapStyle
            ]}
            aria-label={groupLabel}
            role="group"
        >
            {renderMonths()}
        </Animated.View>
    </View>;
};
export default forwardRef(MonthSelector);
